import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { X, ListChecks } from 'lucide-react';
import alertService from '../../utils/alert';
import { fetchActivityTemplates, applyActivityTemplate } from '../../redux/crmSlice';

// Reusable across Lead/Deal/Contact detail pages — pass exactly one of
// leadId/dealId/contactId depending on which entity this modal is attached to.
export default function ApplyTemplateModal({ isOpen, onClose, leadId, dealId, contactId, onApplied }) {
  const dispatch = useDispatch();
  const { activityTemplates } = useSelector(state => state.crm);
  const [templateId, setTemplateId] = useState('');
  const [startDate, setStartDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [applying, setApplying] = useState(false);

  useEffect(() => {
    if (isOpen) dispatch(fetchActivityTemplates());
  }, [isOpen, dispatch]);

  useEffect(() => {
    if (isOpen && activityTemplates.length && !templateId) {
      setTemplateId(activityTemplates[0]._id);
    }
  }, [isOpen, activityTemplates, templateId]);

  if (!isOpen) return null;

  const handleApply = async () => {
    if (!templateId) return alertService.error('Error', 'Select a template first');
    setApplying(true);
    try {
      const result = await dispatch(applyActivityTemplate({ id: templateId, leadId, dealId, contactId, startDate })).unwrap();
      alertService.success('Template Applied', `${result.count} task${result.count === 1 ? '' : 's'} created`);
      onApplied?.();
      onClose();
    } catch (err) {
      alertService.error('Error', err || 'Failed to apply template');
    } finally {
      setApplying(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="bg-white rounded-t-lg sm:rounded-lg shadow-[0_2px_8px_rgba(0,0,0,0.16)] w-full max-w-md z-10 max-h-[90vh] overflow-y-auto animate-in zoom-in-95 duration-200">
        <div className="px-4 sm:px-6 py-3 border-b border-[#E5E5E5] flex items-center justify-between bg-white">
          <h3 className="text-[16px] font-bold text-[#2E2E2E] flex items-center gap-2"><ListChecks size={16} className="text-[#0176D3]" /> Apply Activity Template</h3>
          <button onClick={onClose} className="text-[#706E6B] hover:text-[#080707]"><X size={18} /></button>
        </div>

        <div className="p-4 sm:p-6 space-y-4">
          <div className="space-y-1.5">
            <label className="text-[11px] font-semibold text-[#706E6B]">Template</label>
            <select
              value={templateId}
              onChange={e => setTemplateId(e.target.value)}
              className="w-full h-9 px-3 bg-white border border-[#D8DDE6] rounded text-[13px] outline-none focus:border-[#0176D3] focus:ring-1 focus:ring-[#0176D3]"
            >
              {activityTemplates.length === 0 && <option value="">No templates available</option>}
              {activityTemplates.map(t => (
                <option key={t._id} value={t._id}>{t.name} ({(t.steps || []).length} steps)</option>
              ))}
            </select>
          </div>

          <div className="space-y-1.5">
            <label className="text-[11px] font-semibold text-[#706E6B]">Start Date</label>
            <input
              type="date"
              value={startDate}
              onChange={e => setStartDate(e.target.value)}
              className="w-full h-9 px-3 bg-white border border-[#D8DDE6] rounded text-[13px] outline-none focus:border-[#0176D3] focus:ring-1 focus:ring-[#0176D3]"
            />
            <p className="text-[11px] text-[#706E6B]">Step due dates are calculated relative to this date.</p>
          </div>

          <div className="pt-4 border-t border-[#E5E5E5] flex justify-end gap-2">
            <button type="button" onClick={onClose} className="bg-white h-9 px-4 border border-[#C9C9C9] rounded text-[13px] font-semibold text-[#0176D3] hover:bg-[#F3F2F2]">Cancel</button>
            <button
              type="button"
              onClick={handleApply}
              disabled={applying || !templateId}
              className="h-9 px-4 bg-[#0176D3] text-white rounded text-[13px] font-semibold hover:bg-[#014486] disabled:opacity-50"
            >
              {applying ? 'Applying…' : 'Apply Template'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
